/**
 * Shapes the CI portal API returns and accepts. Shared by the API (src/api),
 * the mock (src/mock), the web app (src/web) and the Cypress tasks.
 * Dates are ISO strings so the same type works on both sides of JSON.
 */

/** How a route's path is compared with the request path. */
export type MockRouteMatchType = "exact" | "prefix";

/** HTTP method a route answers; `*` answers every method. */
export type MockRouteMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "PATCH"
  | "DELETE"
  | "HEAD"
  | "OPTIONS"
  | "*";

export const MOCK_ROUTE_METHODS: MockRouteMethod[] = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "HEAD",
  "OPTIONS",
  "*",
];

/** CI run that created a mock (all empty for mocks made in the portal). */
export type MockTokenCiContext = {
  /** GITHUB_RUN_ID of the workflow run */
  githubRunnerId: string | null;
  /** GITHUB_RUN_ATTEMPT of the workflow run */
  githubRunAttempt: string | null;
  /** Cypress retry attempt that created the mock */
  attemptNo: number | null;
  /** Spec path relative to the repo root */
  testSuite: string | null;
  /** Test title */
  testName: string | null;
};

/** Signed-in user who created a mock. */
export type MockTokenOwner = {
  /** CiUser id */
  id: string;
  /** Display name */
  name: string;
  /** Email address */
  email: string;
};

/** One mock: a token that prefixes every URL it answers. */
export type MockToken = {
  /** Token, the first path segment of every mock URL */
  uuid: string;
  /** Human label shown in the portal */
  label: string | null;
  /** When it was created */
  createdAt: string;
  /** After this it answers 410 Gone; null never expires */
  expiresAt: string | null;
  /** Time of the most recent request it received */
  lastRequestAt: string | null;
  /** Portal user who created it; null for mocks created by CI */
  createdBy: MockTokenOwner | null;
  /** CI run that created it */
  ci: MockTokenCiContext;
  /** Number of routes */
  routeCount: number;
  /** Number of recorded requests */
  requestCount: number;
};

/** A canned response of a mock. */
export type MockRoute = {
  /** Route id */
  uuid: string;
  /** Mock the route belongs to */
  tokenUuid: string;
  /** Path after the token, always starting with `/` */
  requestPath: string;
  /** Method it answers */
  requestMethod: MockRouteMethod;
  /** How requestPath is compared */
  matchType: MockRouteMatchType;
  /** Status code of the response */
  responseStatus: number;
  /** Content-Type of the response */
  responseType: string | null;
  /** Response body */
  responseContent: string | null;
  /** Extra response headers */
  responseHeaders: Record<string, string>;
  /** Milliseconds to wait before answering (0 to 10000) */
  delayMs: number;
  /** Note shown in the portal */
  description: string | null;
  /** When it was created */
  createdAt: string;
  /** When it last changed */
  updatedAt: string;
};

/** A mock with its routes. */
export type MockTokenDetail = MockToken & {
  /** Routes, most specific first */
  routes: MockRoute[];
};

/** A request a mock received. */
export type MockRequest = {
  /** Sequence id; larger is newer. A string because it is a BIGINT */
  id: string;
  /** Mock that received it */
  tokenUuid: string;
  /** Route that answered; null when nothing matched */
  routeUuid: string | null;
  /** HTTP method */
  method: string;
  /** Path after the token */
  path: string;
  /** Raw query string without `?` */
  queryString: string | null;
  /** Request headers, lower-case names */
  headers: Record<string, string>;
  /** Body as text, or base64 when bodyIsBase64 */
  body: string | null;
  /** Whether body is base64 (binary payloads) */
  bodyIsBase64: boolean;
  /** Whether body was cut at the storage limit */
  bodyTruncated: boolean;
  /** Caller's IP address */
  sourceIp: string | null;
  /** Status the mock answered with */
  responseStatus: number;
  /** When it arrived */
  receivedAt: string;
};

/** Fields to create or change a mock. */
export type MockTokenInput = {
  /** Human label */
  label?: string | null;
  /** Expiry; null never expires */
  expiresAt?: string | null;
  /** CI run that creates it */
  ci?: Partial<MockTokenCiContext>;
};

/** Fields to create or change a route. */
export type MockRouteInput = {
  /** Path after the token; a leading `/` is added when missing */
  requestPath: string;
  /** Method it answers */
  requestMethod: MockRouteMethod;
  /** How requestPath is compared */
  matchType?: MockRouteMatchType;
  /** Status code of the response */
  responseStatus?: number;
  /** Content-Type of the response */
  responseType?: string | null;
  /** Response body */
  responseContent?: string | null;
  /** Extra response headers */
  responseHeaders?: Record<string, string>;
  /** Milliseconds to wait before answering */
  delayMs?: number;
  /** Note shown in the portal */
  description?: string | null;
};

/** A page of results. */
export type Page<T> = {
  /** Rows of this page */
  items: T[];
  /** Rows matching the filter, across all pages */
  total: number;
};

/** Settings the web app needs, from GET /api/config. */
export type CiPortalConfig = {
  /** Public base URL of the mock lambda, e.g. https://xyz.lambda-url.us-east-1.on.aws */
  mockBaseUrl: string | null;
  /** Identity providers offered on the sign-in page */
  providers: CiPortalProvider[];
  /** Local development only: email/password sign-in is enabled */
  devPasswordLogin: boolean;
};

/** A sign-in option. */
export type CiPortalProvider = {
  /** Better Auth provider id, e.g. `microsoft` */
  id: string;
  /** Button label */
  label: string;
};
